import React, { Fragment, useState, useRef, useEffect } from "react";
import { showHttpError, showToast, currentPageAccess } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";

// ─────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────
const DEFAULT_BASE_URL = `${window.location.origin}/merchant/checkout/`;
const buildCheckoutBase = (domain) => {
  if (domain && typeof domain === "string" && domain.trim() !== "") {
    const d = domain.endsWith("/") ? domain : `${domain}/`;
    return `${d}payments/merchant/checkout/`;
  }
  return DEFAULT_BASE_URL;
};
const API_BASE = WEBSERVICE;

const getMerchantId = () =>
  localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getToken = () =>
  getAuthToken() || "";
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
  UUID: getAuthUuid() || "",
});

const apiGet = (url) =>
  fetch(url, { headers: authHeaders() }).then((r) => {
    if (!r.ok) throw { response: { status: r.status } };
    return r.json();
  });
const apiPost = (url, body) =>
  fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then((r) => {
    if (!r.ok) throw { response: { status: r.status } };
    return r.json();
  });

const fetchShippingProfiles = (mid) =>
  apiGet(`${API_BASE}/api/merchants/${mid}/shipping-profiles`);
const fetchDiscountProfiles = (mid) =>
  apiGet(`${API_BASE}/shopping/discounts?merchantId=${mid}`);
const fetchCatalogs = (mid) => apiGet(`${API_BASE}/shopping/catalogs/${mid}`);
const fetchBuyerInfoProfiles = (mid) =>
  apiGet(`${API_BASE}/buyer-information/${mid}`);
const fetchAllProducts = (mid) =>
  apiGet(`${API_BASE}/shopping/products?merchantId=${mid}&&page=1&size=100&status=ACTIVE`);
const fetchPaymentOptions = (mid) =>
  apiGet(`${API_BASE}/billing-profile/${mid}`);
const fetchMerchantSettings = (mid) =>
  apiPost(`${API_BASE}/merchant/FetchMerchantsettings`, {
    merchant_id: String(mid),
  });
const fetchProcessingFees = (mid) =>
  apiGet(`${API_BASE}/processing-fee/status/${mid}`);
const fetchRedirectProfiles = (mid) =>
  apiGet(`${API_BASE}/redirect-templates?merchantId=${mid}`);
const fetchPaymentGateways = (mid) =>
  apiPost(`${API_BASE}/merchant/getAllPaymentGateway`, {
    merchantId: String(mid),
  });
const savePaymentLink = (p) => apiPost(`${API_BASE}/payment/create`, p);
const fetchRewardsCampaigns = (mid) =>
  apiGet(`${API_BASE}/campaigns/merchant?merchantId=${mid}&status=active`);

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
function formatPrice(p) {
  if (!p) return "—";
  const amt = parseFloat(p.amount || p.basePrice || p.price || 0).toFixed(2);
  const cur = p.currency || "USD";
  if (p.priceType === "subscription" || p.priceType === "recurring") {
    const int = p.billingInterval || p.recurringInterval || "month";
    return `${cur} ${amt} / ${int}`;
  }
  return `${cur} ${amt}`;
}

// ─────────────────────────────────────────────────────────────────
// Toast
// ─────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
  if (!msg) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        background: type === "success" ? "#10b981" : "#ef4444",
        color: "#fff",
        padding: "12px 20px",
        borderRadius: "12px",
        fontSize: "13px",
        fontWeight: "600",
        boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        animation: "slideUp .2s ease",
      }}
    >
      <i
        className={`mdi ${
          type === "success" ? "mdi-check-circle" : "mdi-alert-circle"
        }`}
      />
      {msg}
    </div>
  );
}

function EmptyProfileRow({ label, color, redirectPath, btnLabel }) {
  if (!redirectPath)
    return (
      <div
        style={{
          padding: "10px 14px",
          borderRadius: "8px",
          border: "1.5px dashed rgba(99,102,241,.25)",
          background: "transparent",
        }}
      >
        <span
          style={{
            fontSize: "13px",
            color: "var(--text-color)",
            opacity: 0.45,
          }}
        >
          No {label.toLowerCase()} found
        </span>
      </div>
    );
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderRadius: "8px",
        border: "1.5px dashed rgba(99,102,241,.4)",
        background: "transparent",
        gap: 12,
      }}
    >
      <span
        style={{ fontSize: "13px", color: "var(--text-color)", opacity: 0.5 }}
      >
        No {label.toLowerCase()} found
      </span>
      <button
        type="button"
        onClick={() => {
          window.location.href = redirectPath;
        }}
        style={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "8px 18px",
          borderRadius: "8px",
          border: "none",
          background: "var(--btn-color)",
          color: "#fff",
          fontSize: "13px",
          fontWeight: "600",
          cursor: "pointer",
          boxShadow:
            "0 0 12px color-mix(in srgb,var(--btn-color) 35%,transparent)",
          transition: "filter .15s",
        }}
      >
        + {btnLabel || `Create ${label}`}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SubRow — updated: left-aligned pills, detailTags for selected state
// ─────────────────────────────────────────────────────────────────
function SubRow({
  label,
  options,
  value,
  onChange,
  optional,
  color = "#6366f1",
  redirectPath,
  btnLabel,
  defaultTags,
  feeHandlingDefault,
  selectedDetailTags,
  useRedirectIcon = false,
  showUserDefaultLabel = true,
  userDefaultProperty = "isDefault",
}) {
  const isDefault =
    !value || value === "" || value === null || value === undefined;

  return (
    <div
      className="cp-subrow"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        padding: "11px 0",
        borderBottom: "1px solid var(--body-color-less2)",
        flexWrap: "wrap",
      }}
    >
      {/* Label - now stays on one line */}
      <span
        className="cp-subrow-label"
        style={{
          flex: "0 0 auto",
          minWidth: "120px",
          fontSize: "13px",
          fontWeight: "500",
          color: "var(--text-color)",
          paddingTop: 8,
          whiteSpace: "nowrap",
        }}
      >
        {label}
        {optional && (
          <span
            style={{
              marginLeft: "6px",
              fontSize: "10px",
              background: "rgba(100,116,139,.12)",
              color: "var(--text-color)",
              opacity: 0.5,
              borderRadius: "4px",
              padding: "1px 5px",
              whiteSpace: "nowrap",
            }}
          >
            optional
          </span>
        )}
      </span>

      {/* Right side — dropdown + pills stacked, takes full width */}
      <div
        className="cp-subrow-right"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: "1",
          minWidth: 0,
        }}
      >
        {/* Dropdown row — right aligned */}
        <div
          className="cp-subrow-dropdown"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <select
            className="cp-subrow-sel"
            value={value !== null && value !== undefined ? String(value) : ""}
            onChange={(e) => {
              const v = e.target.value;
              if (!v || v === "") {
                onChange(null);
                return;
              }
              onChange(v);
            }}
            disabled={options.length === 0}
            style={{
              height: "36px",
              background: "var(--body-color)",
              border: "1.5px solid var(--body-color-less2)",
              borderRadius: "8px",
              padding: "0 12px",
              opacity: options.length === 0 ? 0.45 : 1,
              color: "var(--text-color)",
              fontSize: "13px",
              outline: "none",
              cursor: options.length === 0 ? "not-allowed" : "pointer",
              minWidth: "180px",
              appearance: "auto",
            }}
          >
            <option value="">— Sys Default —</option>
            {options.map((o) => {
              let isUserDefault = false;
              if (userDefaultProperty === "isProcessingFeeEnabled") {
                isUserDefault = o.isProcessingFeeEnabled === true;
              } else {
                isUserDefault = o.isDefault === 1 || o.isDefaultProfile === 1;
              }
              return (
                <option key={o.id} value={String(o.id)}>
                  {o.name}
                  {showUserDefaultLabel && isUserDefault ? " (User Default)" : ""}
                </option>
              );
            })}
          </select>

          {redirectPath ? (
            <button
              type="button"
              title={btnLabel || `Configure ${label}`}
              onClick={(e) => {
                e.preventDefault();
                window.open(redirectPath, "_blank");
              }}
              style={{
                flexShrink: 0,
                width: 32,
                height: 32,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                border:
                  "1.5px solid color-mix(in srgb,var(--btn-color) 35%,transparent)",
                background:
                  "color-mix(in srgb,var(--btn-color) 8%,transparent)",
                color: "var(--btn-color)",
                fontSize: "16px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all .15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "color-mix(in srgb,var(--btn-color) 18%,transparent)";
                e.currentTarget.style.transform = "scale(1.02)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "color-mix(in srgb,var(--btn-color) 8%,transparent)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <i
                className={`mdi ${
                  useRedirectIcon ? "mdi-open-in-new" : "mdi-plus"
                }`}
                style={{
                  fontSize: useRedirectIcon ? "16px" : "18px",
                  fontWeight: useRedirectIcon ? "400" : "600",
                }}
              />
            </button>
          ) : (
            <div style={{ flexShrink: 0, width: 32 }} />
          )}
        </div>

        {/* ── Pills Section — takes full width, right aligned ── */}

        {/* Default tags section */}
        {isDefault && defaultTags && defaultTags.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%",
            }}
          >
            {defaultTags.map((tag, idx) => (
              <span
                key={tag.label + idx}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "6px 16px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "500",
                  background:
                    tag.bg || `color-mix(in srgb,${color} 10%,transparent)`,
                  color: tag.color || color,
                  border: `1px solid ${
                    tag.border || `color-mix(in srgb,${color} 20%,transparent)`
                  }`,
                  whiteSpace: "nowrap",
                  flex: "0 0 auto",
                }}
              >
                {tag.label}
              </span>
            ))}
          </div>
        )}

        {/* Selected detail tags section */}
        {!isDefault &&
          !feeHandlingDefault &&
          selectedDetailTags &&
          selectedDetailTags.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "flex-end",
                alignItems: "center",
                width: "100%",
              }}
            >
              {selectedDetailTags.map((tag, i) => (
                <span
                  key={tag.label + i}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "5px 14px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "500",
                    background:
                      tag.bg || `color-mix(in srgb,${color} 10%,transparent)`,
                    color: tag.color || color,
                    border: `1px solid ${
                      tag.border ||
                      `color-mix(in srgb,${color} 20%,transparent)`
                    }`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          )}

        {/* Fallback name pill section */}
        {!isDefault &&
          !feeHandlingDefault &&
          (!selectedDetailTags || selectedDetailTags.length === 0) && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "flex-end",
                alignItems: "center",
                width: "100%",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "5px 14px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "500",
                  background: `color-mix(in srgb,${color} 10%,transparent)`,
                  color: color,
                  border: `1px solid color-mix(in srgb,${color} 20%,transparent)`,
                  whiteSpace: "nowrap",
                }}
              >
                {options.find((o) => String(o.id) === String(value))?.name ||
                  "Selected"}
              </span>
            </div>
          )}

        {/* Fee handling tag section - shows the current selection */}
        {feeHandlingDefault && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "5px 14px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                background: "rgba(236,72,153,.10)",
                color: "#ec4899",
                border: "1px solid rgba(236,72,153,.22)",
                whiteSpace: "nowrap",
              }}
            >
              {feeHandlingDefault}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function SimpleQR({ value, size = 140 }) {
  return (
    <img
      src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
        value
      )}&size=${size}x${size}&margin=10`}
      alt="QR"
      width={size}
      height={size}
      style={{ borderRadius: "8px", background: "#fff", padding: "4px" }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// ProductCatalogPicker
// ─────────────────────────────────────────────────────────────────
function ProductCatalogPicker({
  products,
  catalogs,
  selectedId,
  onSelect,
  onCatalogSelect,
  selectedCatalogId,
  isReadOnly,
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef();

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const bycat = {};
  products.forEach((p) => {
    const cid = String(p.catalogId || "uncategorised");
    if (!bycat[cid])
      bycat[cid] = {
        name:
          p.catalogName ||
          catalogs.find((c) => String(c.id) === cid)?.name ||
          "Products",
        items: [],
      };
    bycat[cid].items.push(p);
  });

  const selectedProd = products.find((p) => p.id === selectedId);
  const selectedCat = catalogs.find((c) => c.id === selectedCatalogId);
  const triggerLabel = selectedProd
    ? selectedProd.name
    : selectedCat
    ? selectedCat.name
    : "What are you selling?";
  const isPlaceholder = !selectedProd && !selectedCat;

  const triggerStyle = {
    width: "100%",
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 14px",
    fontSize: 14,
    borderRadius: 8,
    border: open
      ? "1.5px solid var(--btn-color)"
      : "1.5px solid var(--body-color-less2)",
    background: "var(--body-color)",
    color: "var(--text-color)",
    cursor: "pointer",
    boxSizing: "border-box",
    boxShadow: open
      ? "0 0 0 3px color-mix(in srgb,var(--btn-color) 14%,transparent)"
      : "none",
    transition: "border-color .15s, box-shadow .15s",
    userSelect: "none",
  };

  const dropStyle = {
    position: "absolute",
    top: "calc(100% + 5px)",
    left: 0,
    right: 0,
    zIndex: 200,
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 10,
    boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
    animation: "dropFade .15s ease",
    maxHeight: "300px",
    overflowY: "auto",
    overflowX: "hidden",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={triggerStyle} onClick={() => setOpen((o) => !o)}>
        <span
          style={{
            opacity: isPlaceholder ? 0.4 : 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {triggerLabel}
        </span>
        <i
          className={`mdi ${open ? "mdi-chevron-up" : "mdi-chevron-down"}`}
          style={{
            fontSize: 16,
            color: "var(--text-color)",
            opacity: 0.5,
            flexShrink: 0,
            marginLeft: 8,
          }}
        />
      </div>

      {open && (
        <div style={dropStyle}>
          {Object.entries(bycat).map(([cid, group]) => (
            <div key={cid}>
              <div
                style={{
                  padding: "8px 14px 4px",
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".7px",
                  color: "var(--text-color)",
                  opacity: 0.35,
                  position: "sticky",
                  top: 0,
                  background: "var(--body-color-less)",
                  zIndex: 1,
                }}
              >
                {group.name}
              </div>
              {group.items.map((p) => {
                const isSelected = p.id === selectedId;
                return (
                  <div
                    key={p.id}
                    onClick={() => {
                      onSelect(p.id);
                      setOpen(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 14px",
                      cursor: "pointer",
                      fontSize: 14,
                      color: isSelected
                        ? "var(--btn-color)"
                        : "var(--text-color)",
                      background: isSelected
                        ? "color-mix(in srgb,var(--btn-color) 8%,transparent)"
                        : "transparent",
                      transition: "background .1s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.background =
                          "color-mix(in srgb,var(--btn-color) 5%,transparent)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span style={{ fontWeight: isSelected ? 600 : 400 }}>
                      {p.name}
                    </span>
                    {isSelected && (
                      <i
                        className="mdi mdi-check"
                        style={{
                          fontSize: 14,
                          color: "var(--btn-color)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {catalogs.length > 0 && (
            <>
              <div
                style={{
                  height: 1,
                  background: "var(--body-color-less2)",
                  margin: "4px 0",
                }}
              />
              <div
                style={{
                  padding: "8px 14px 4px",
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".7px",
                  color: "var(--text-color)",
                  opacity: 0.35,
                  position: "sticky",
                  top: 0,
                  background: "var(--body-color-less)",
                  zIndex: 1,
                }}
              >
                Catalogues
              </div>
              {catalogs.map((cat) => {
                const isSelCat = cat.id === selectedCatalogId;
                return (
                  <div
                    key={cat.id}
                    onClick={() => {
                      onCatalogSelect(cat.id);
                      setOpen(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 14px",
                      fontSize: 14,
                      color: isSelCat
                        ? "var(--btn-color)"
                        : "var(--text-color)",
                      background: isSelCat
                        ? "color-mix(in srgb,var(--btn-color) 8%,transparent)"
                        : "transparent",
                      cursor: "pointer",
                      transition: "background .1s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelCat)
                        e.currentTarget.style.background =
                          "color-mix(in srgb,var(--btn-color) 5%,transparent)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelCat)
                        e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span style={{ fontWeight: isSelCat ? 600 : 400 }}>
                      {cat.name}
                    </span>
                    {isSelCat && (
                      <i
                        className="mdi mdi-check"
                        style={{
                          fontSize: 14,
                          color: "var(--btn-color)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </>
          )}

          <div
            style={{
              height: 1,
              background: "var(--body-color-less2)",
              margin: "4px 0",
            }}
          />
          <div
            onClick={() => {
              if (isReadOnly) {
                showToast("error", "You have read-only access to this page");
                return;
              }
              window.dispatchEvent(new CustomEvent("openQuickPayment"));
            }}
            style={{
              padding: "10px 14px",
              fontSize: 14,
              fontStyle: "italic",
              color: "var(--btn-color)",
              cursor: isReadOnly ? "not-allowed" : "pointer",
              opacity: isReadOnly ? 0.5 : 1,
              transition: "background .1s",
            }}
            onMouseEnter={(e) => {
              if (!isReadOnly)
                e.currentTarget.style.background =
                  "color-mix(in srgb,var(--btn-color) 6%,transparent)";
            }}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            Quick Payment
          </div>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ icon, color, text, optional, noMargin }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: noMargin ? 0 : "12px",
      }}
    >
      <i
        className={`mdi ${icon}`}
        style={{ fontSize: "14px", color, opacity: 0.8 }}
      />
      <span
        style={{
          fontSize: "13px",
          fontWeight: "600",
          color: "var(--text-color)",
          opacity: 0.7,
        }}
      >
        {text}
      </span>
      {optional && (
        <span
          style={{
            fontSize: "10px",
            fontWeight: "500",
            background: "rgba(100,116,139,.12)",
            color: "var(--text-color)",
            opacity: 0.5,
            borderRadius: "4px",
            padding: "1px 6px",
          }}
        >
          optional
        </span>
      )}
    </div>
  );
}

// Style tokens
const CARD = {
  background: "var(--body-color-less)",
  border: "1px solid var(--body-color-less2)",
  borderRadius: "12px",
  padding: "18px 20px",
  marginBottom: "16px",
  overflow: "visible",
};
const RIGHT_CARD = {
  background: "var(--body-color-less)",
  border: "1px solid var(--body-color-less2)",
  borderRadius: "12px",
  padding: "18px",
};
const RIGHT_LABEL = {
  fontSize: "10px",
  fontWeight: "700",
  textTransform: "uppercase",
  letterSpacing: ".7px",
  color: "var(--text-color)",
  opacity: 0.45,
  marginBottom: "12px",
};
const FIELD_LABEL = {
  fontSize: "11px",
  fontWeight: "700",
  textTransform: "uppercase",
  letterSpacing: ".5px",
  color: "var(--text-color)",
  opacity: 0.45,
  marginBottom: "7px",
  display: "block",
};

// ─────────────────────────────────────────────────────────────────
// Helper: build detail pills for each profile type
// ─────────────────────────────────────────────────────────────────

// pmId → display info
const PM_META = {
  1: {
    label: "Card (PayBito Apex)",
    icon: "mdi-credit-card-outline",
    bg: "rgba(99,102,241,.10)",
    color: "#6366f1",
    border: "rgba(99,102,241,.22)",
  },
  2: {
    label: "PayBito Titan",
    icon: "mdi-alpha-p-circle-outline",
    bg: "rgba(245,158,11,.10)",
    color: "#f59e0b",
    border: "rgba(245,158,11,.22)",
  },
  3: {
    label: "Crypto - Brand Wallet",
    icon: "mdi-wallet-outline",
    bg: "rgba(16,185,129,.10)",
    color: "#10b981",
    border: "rgba(16,185,129,.22)",
  },
  4: {
    label: "Crypto - External Wallet",
    icon: "mdi-link-variant",
    bg: "rgba(20,184,166,.10)",
    color: "#14b8a6",
    border: "rgba(20,184,166,.22)",
  },
  5: {
    label: "Crypto - Guest Checkout",
    icon: "mdi-account-outline",
    bg: "rgba(139,92,246,.10)",
    color: "#8b5cf6",
    border: "rgba(139,92,246,.22)",
  },
  7: {
    label: "PayBito Nexus",
    icon: "mdi-credit-card-outline",
    bg: "rgba(99,102,241,.10)",
    color: "#6366f1",
    border: "rgba(99,102,241,.22)",
  },
  8: {
    label: "PayBito Vertex",
    icon: "mdi-credit-card-outline",
    bg: "rgba(99,102,241,.10)",
    color: "#6366f1",
    border: "rgba(99,102,241,.22)",
  },
  9: {
    label: "PayBito Nova",
    icon: "mdi-credit-card-outline",
    bg: "rgba(99,102,241,.10)",
    color: "#6366f1",
    border: "rgba(99,102,241,.22)",
  },
  11: {
    label: "PayBito Zenith",
    icon: "mdi-flash",
    bg: "rgba(124,58,237,.10)",
    color: "#7c3aed",
    border: "rgba(124,58,237,.22)",
  },
};

function buildPaymentOptionTags(profileId, paymentOptionProfilesFull, isSubscription = false) {
  if (!profileId) return [];
  const profile = paymentOptionProfilesFull.find(
    (p) =>
      String(p.id) === String(profileId) ||
      String(p.billingProfileId) === String(profileId)
  );
  if (!profile) return [];
  const methods = profile.billingMethod || profile.paymentMethods || [];
  if (!methods.length) return [];

  // Only show payment methods that are enabled (status === 1)
  let filteredMethods = methods.filter((m) => Number(m.status) === 1);
  if (isSubscription) {
    filteredMethods = filteredMethods.filter((m) => {
      const pmId = Number(m.pmId || m.paymentMethodId || m.id);
      return [1, 2, 3, 7, 8, 9, 11].includes(pmId);
    });
  }

  return filteredMethods.map((m) => {
    const pmId = Number(m.pmId || m.paymentMethodId || m.id);
    const meta = PM_META[pmId];
    if (!meta)
      return {
        label: m.name || `Method ${pmId}`,
        icon: "mdi-credit-card-outline",
        bg: "rgba(99,102,241,.10)",
        color: "#6366f1",
        border: "rgba(99,102,241,.22)",
      };
    return meta;
  });
}

function buildBuyerInfoTags(profileId, buyerInfoProfilesFull) {
  if (!profileId || !buyerInfoProfilesFull?.length) return [];
  const profile = buyerInfoProfilesFull.find(
    (p) => String(p.id) === String(profileId)
  );
  if (!profile) return [];

  const tags = [];

  // Map of field keys to display labels
  const fieldMap = {
    collectEmail: { label: "Email", icon: "mdi-email-outline" },
    collectFullName: { label: "Full Name", icon: "mdi-account-outline" },
    collectPhoneNumber: { label: "Phone Number", icon: "mdi-phone-outline" },
    collectAddress: { label: "Address", icon: "mdi-map-marker-outline" },
    collectCompanyName: { label: "Company Name", icon: "mdi-domain-outline" },
    collectOrderNotes: { label: "Order Notes", icon: "mdi-note-text-outline" },
    collectTaxInfo: { label: "Tax Info", icon: "mdi-receipt-outline" },
    collectCryptoRefundAddress: {
      label: "Crypto Refund Address",
      icon: "mdi-currency-btc",
    },
  };

  // Check each field and add if value is 1
  Object.entries(fieldMap).forEach(([fieldKey, fieldInfo]) => {
    if (profile[fieldKey] === 1 || profile[fieldKey] === "1") {
      tags.push({
        label: fieldInfo.label,
        icon: fieldInfo.icon,
        bg: "rgba(139,92,246,.10)",
        color: "#8b5cf6",
        border: "rgba(139,92,246,.22)",
      });
    }
  });

  // Handle custom fields
  const customFields = profile.customFields || [];
  if (customFields.length > 0) {
    tags.push({
      label: `Custom Fields (${customFields.length})`,
      icon: "mdi-form-select",
      bg: "rgba(139,92,246,.12)",
      color: "#a78bfa",
      border: "rgba(139,92,246,.25)",
    });
  }

  // If no fields collected, show a default pill with profile name
  if (tags.length === 0) {
    tags.push({
      label: profile.profileName || "Buyer Info",
      icon: "mdi-account-check-outline",
      bg: "rgba(139,92,246,.10)",
      color: "#8b5cf6",
      border: "rgba(139,92,246,.22)",
    });
  }

  return tags;
}

function buildShippingTags(profileId, shippingProfilesFull) {
  if (!profileId || !shippingProfilesFull?.length) return [];
  const profile = shippingProfilesFull.find(
    (p) => String(p.id) === String(profileId)
  );
  if (!profile) return [];

  const tags = [];

  // Handling fee / shipping rate
  const handlingFee =
    profile.handlingFeeValue || profile.shippingRate || profile.rateValue;
  const handlingFeeType =
    profile.handlingFeeType || profile.rateType || "percentage";

  if (handlingFee !== undefined && handlingFee !== null && handlingFee !== "") {
    const feeLabel =
      handlingFeeType === "percentage"
        ? `${handlingFee}% Handling Fee`
        : `$${handlingFee} Shipping`;
    tags.push({
      label: feeLabel,
      icon: "mdi-truck-delivery-outline",
      bg: "rgba(59,130,246,.10)",
      color: "#3b82f6",
      border: "rgba(59,130,246,.22)",
    });
  }

  // Tax rate
  const taxRate = profile.taxRate || profile.taxPercentage;
  if (
    taxRate !== undefined &&
    taxRate !== null &&
    taxRate !== "" &&
    parseFloat(taxRate) > 0
  ) {
    tags.push({
      label: `Tax: ${taxRate}%`,
      icon: "mdi-receipt-outline",
      bg: "rgba(59,130,246,.08)",
      color: "#60a5fa",
      border: "rgba(59,130,246,.18)",
    });
  }

  // If no tags created, show profile name
  if (tags.length === 0) {
    tags.push({
      label: profile.profileName || "Shipping Profile",
      icon: "mdi-truck-delivery-outline",
      bg: "rgba(59,130,246,.10)",
      color: "#3b82f6",
      border: "rgba(59,130,246,.22)",
    });
  }

  return tags;
}

function buildDiscountTags(profileId, discountProfilesFull) {
  if (!profileId || !discountProfilesFull?.length) return [];
  const profile = discountProfilesFull.find(
    (p) => String(p.id) === String(profileId)
  );
  if (!profile) return [];

  // Find all profiles with the same name
  const allRulesWithSameName = discountProfilesFull.filter(
    (p) => p.profileName === profile.profileName
  );

  const tags = [];

  // Build tags for each rule with the same profile name
  allRulesWithSameName.forEach((rule) => {
    // Get discount percentage
    const discountPct =
      rule.discountPercentage ||
      rule.discountPercent ||
      rule.discountValue;

    // Get minimum cart value
    const minCartValue =
      rule.minimumCartValue || rule.minCartValue || rule.minimumAmount;

    // Build the discount label
    if (discountPct) {
      let discountLabel = `${discountPct}% Off`;
      if (minCartValue && parseFloat(minCartValue) > 0) {
        discountLabel = `${discountPct}% Off (Min. $${minCartValue})`;
      }
      tags.push({
        label: discountLabel,
        icon: "mdi-tag-outline",
        bg: "rgba(34,197,94,.10)",
        color: "#22c55e",
        border: "rgba(34,197,94,.22)",
      });
    }

    // Add coupon code if present
    if (rule.couponCode) {
      tags.push({
        label: `Code: ${rule.couponCode}`,
        icon: "mdi-ticket-percent-outline",
        bg: "rgba(34,197,94,.08)",
        color: "#4ade80",
        border: "rgba(34,197,94,.18)",
      });
    }
  });

  // If no discount info found, show profile name as fallback
  if (tags.length === 0) {
    tags.push({
      label: profile.profileName || "Discount",
      icon: "mdi-tag-multiple-outline",
      bg: "rgba(34,197,94,.10)",
      color: "#22c55e",
      border: "rgba(34,197,94,.22)",
    });
  }

  return tags;
}

function buildRedirectTags(profileId, redirectProfilesFull) {
  if (!profileId || !redirectProfilesFull?.length) return [];
  const profile = redirectProfilesFull.find(
    (p) => String(p.id) === String(profileId)
  );
  if (!profile) return [];

  const tags = [];

  // Success URL
  const successUrl = profile.successUrl || profile.successRedirectUrl;
  if (successUrl) {
    const shortUrl = successUrl.replace(/^https?:\/\//, "").slice(0, 30);
    tags.push({
      label: `✓ ${shortUrl}${successUrl.length > 30 ? "…" : ""}`,
      icon: "mdi-check-circle-outline",
      bg: "rgba(16,185,129,.10)",
      color: "#10b981",
      border: "rgba(16,185,129,.22)",
    });
  }

  // Failure URL
  const failureUrl = profile.failureUrl || profile.failureRedirectUrl;
  if (failureUrl) {
    const shortUrl = failureUrl.replace(/^https?:\/\//, "").slice(0, 30);
    tags.push({
      label: `✗ ${shortUrl}${failureUrl.length > 30 ? "…" : ""}`,
      icon: "mdi-close-circle-outline",
      bg: "rgba(239,68,68,.10)",
      color: "#ef4444",
      border: "rgba(239,68,68,.22)",
    });
  }

  // If no URLs found, show template name
  if (tags.length === 0) {
    tags.push({
      label: profile.templateName || "Redirect",
      icon: "mdi-redo-variant",
      bg: "rgba(20,184,166,.10)",
      color: "#14b8a6",
      border: "rgba(20,184,166,.22)",
    });
  }

  return tags;
}

function buildRewardsTags(campaignId, rewardsCampaignsFull) {
  if (!campaignId || !rewardsCampaignsFull?.length) return [];
  const c = rewardsCampaignsFull.find(
    (r) => String(r.campaignId) === String(campaignId)
  );
  if (!c) return [];
  const tags = [];
  if (c.rewardType) {
    tags.push({
      label: String(c.rewardType).replace(/\b\w/g, (m) => m.toUpperCase()),
      icon: "mdi-gift-outline",
      bg: "rgba(234,179,8,.12)",
      color: "#eab308",
      border: "rgba(234,179,8,.24)",
    });
  }
  if (c.rewardRate !== undefined && c.rewardRate !== null) {
    tags.push({
      label: `${c.rewardRate}% Rate`,
      icon: "mdi-percent-outline",
      bg: "rgba(234,179,8,.10)",
      color: "#ca8a04",
      border: "rgba(234,179,8,.22)",
    });
  }
  return tags;
}

// ─────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────
export default function CreatePayment() {
  const [selections, setSelections] = useState({
    catalog: null,
    shipping: null,
    buyerInfo: null,
    discount: null,
    paymentOption: null,
    feeHandling: null,
    redirect: null,
    rewards: null,
  });
  const [copiedId, setCopiedId] = useState(false);
  const [paymentName, setPaymentName] = useState("");
  const [generated, setGenerated] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [activeTab, setActiveTab] = useState("link");
  const [buttonSize, setButtonSize] = useState("medium");
  const [htmlCode, setHtmlCode] = useState("");
  const [toast, setToast] = useState({ msg: "", type: "success" });
  const [loading, setLoading] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const permission = currentPageAccess();
  const isReadOnly = permission === "READ";
  const [copied, setCopied] = useState(false);
  const [checkoutBaseUrl, setCheckoutBaseUrl] = useState(DEFAULT_BASE_URL);
  const [emailList, setEmailList] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const textareaRef = useRef(null);

  // Product selection
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProdId, setSelectedProdId] = useState(null);
  const [selectedPriceId, setSelectedPriceId] = useState(null);

  // Profiles — dropdown options (id+name)
  const [shippingProfiles, setShippingProfiles] = useState([]);
  const [discountProfiles, setDiscountProfiles] = useState([]);
  const [catalogProfiles, setCatalogProfiles] = useState([]);
  const [buyerInfoProfiles, setBuyerInfoProfiles] = useState([]);
  const [paymentOptionProfiles, setPaymentOptionProfiles] = useState([]);
  const [paymentOptionProfilesFull, setPaymentOptionProfilesFull] = useState(
    []
  );
  const [redirectProfiles, setRedirectProfiles] = useState([]);
  const [rewardsCampaigns, setRewardsCampaigns] = useState([]);
  const [rewardsCampaignsFull, setRewardsCampaignsFull] = useState([]);

  // Full raw data for pill generation
  const [shippingProfilesFull, setShippingProfilesFull] = useState([]);
  const [discountProfilesFull, setDiscountProfilesFull] = useState([]);
  const [buyerInfoProfilesFull, setBuyerInfoProfilesFull] = useState([]);
  const [redirectProfilesFull, setRedirectProfilesFull] = useState([]);

  const [hasStripe, setHasStripe] = useState(false);
  const [hasPayPal, setHasPayPal] = useState(false);
  const [hasKurvPay, setHasKurvPay] = useState(false);
  const [hasNetBilling, setHasNetBilling] = useState(false);
  const [hasHMS, setHasHMS] = useState(false);
  const [hasNMI, setHasNMI] = useState(false);
  const [merchantFeeDefault, setMerchantFeeDefault] = useState("1");
  const [isProcessingFeeEnabled, setIsProcessingFeeEnabled] = useState(1); // Default to 1 (Customer Pays)

  

  // Replace the getFeeOptions function with this simpler version
  const getFeeOptions = () => {
    return [
      { id: "0", name: "Merchant Pays", isProcessingFeeEnabled: isProcessingFeeEnabled === 0 },
      { id: "1", name: "Customer Pays", isProcessingFeeEnabled: isProcessingFeeEnabled === 1 }
    ];
  };

  // Fee handling change handler
  const handleFeeHandlingChange = (v) => {
    console.log("Fee handling changed to:", v); // Debug log
    setSelections((s) => ({ ...s, feeHandling: v }));
  };

  // Get the display label for the current selection
  const getFeeHandlingDisplayLabel = (selection, isProcessingFeeEnabled) => {
    const isCustomerPaysDefault =
      isProcessingFeeEnabled === 1 || isProcessingFeeEnabled === "1";

    if (!selection || selection === "" || selection === null) {
      return isCustomerPaysDefault ? "Customer Pays" : "Merchant Pays";
    }

    if (selection === "1" || selection === 1) return "Customer Pays";
    if (selection === "0" || selection === 0) return "Merchant Pays";

    return isCustomerPaysDefault ? "Customer Pays" : "Merchant Pays";
  };

  useEffect(() => {
    const mid = getMerchantId();
    fetchProcessingFees(mid)
      .then((res) => {
        const row = res.data || {};
        const flag = row?.isProcessingFeeEnabled;
        const enabled = flag === "1" || flag === 1 ? 1 : 0;
        setIsProcessingFeeEnabled(enabled);

        // Set default fee handling based on API response
        // If enabled (Customer Pays default), set to "1", else set to "0"
        const defaultFeeValue = enabled === 1 ? "1" : "0";
        console.log("Setting default fee handling to:", defaultFeeValue);
        setSelections((s) => ({ ...s, feeHandling: defaultFeeValue }));
      })
      .catch((e) => showHttpError(e));
  }, []);

  // Add this state near your other useState declarations

  // Add this useEffect near your other fetch calls (around where fetchProcessingFees is called)
  useEffect(() => {
    const mid = getMerchantId();
    fetchProcessingFees(mid)
      .then((res) => {
        const row = res.data || [];
        const flag = row?.isProcessingFeeEnabled;
        const enabled = flag === "1" || flag === 1 ? 1 : 0;
        setIsProcessingFeeEnabled(enabled);

        // Set default fee handling based on API response
        const defaultFeeValue = enabled === 1 ? "1" : "0"; // Customer pays if enabled, else Merchant pays
        setSelections((s) => ({ ...s, feeHandling: defaultFeeValue }));
      })
      .catch((e) => showHttpError(e));
  }, []);

  // ── Load profiles ──────────────────────────────────────────────
  useEffect(() => {
    const mid = getMerchantId();

    fetchPaymentGateways(mid)
      .then((res) => {
        if (res.error !== 0) return;
        const gws = res.payment_gateways || [];
        setHasStripe(
          gws.some((g) => g.gatewayName === "Stripe" && g.isActive === 1)
        );
        setHasPayPal(
          gws.some((g) => g.gatewayName === "Paypal" && g.isActive === 1)
        );
        setHasKurvPay(
          gws.some((g) => g.gatewayName === "KurvPay" && g.isActive === 1)
        );
        setHasNetBilling(
          gws.some((g) => g.gatewayName.toLowerCase() === "netbilling" && g.isActive === 1)
        );
        setHasHMS(
          gws.some((g) => g.gatewayName === "HostMerchantServices" && g.isActive === 1)
        );
        setHasNMI(
          gws.some((g) => g.gatewayName === "NMI" && g.isActive === 1)
        );
      })
      .catch((e) => showHttpError(e));

    fetchShippingProfiles(mid)
      .then((res) => {
        if (res.error !== 0) return;
        const active = (res.data || []).filter((p) => p.isActive === 1);
        setShippingProfilesFull(active); // store full for pills
        setShippingProfiles(
          active.map((p) => ({ id: p.id, name: p.profileName, isDefaultProfile: p.isDefaultProfile }))
        );
        const defaultShipping = active.find((p) => p.isDefaultProfile === 1);
        if (defaultShipping) {
          setSelections((s) => ({ ...s, shipping: defaultShipping.id }));
        }
      })
      .catch((e) => showHttpError(e));

    fetchDiscountProfiles(mid)
      .then((res) => {
        if (!res.status) return;
        setDiscountProfilesFull(res.data || []); // store ALL profiles for pills

        // Deduplicate for dropdown by profile name, prioritizing default profile
        const profilesByName = {};
        (res.data || []).forEach((r) => {
          if (!profilesByName[r.profileName]) {
            profilesByName[r.profileName] = r;
          } else if (r.isDefaultProfile === 1 || r.isDefaultProfile === true) {
            // Replace with default if this one is the default
            profilesByName[r.profileName] = r;
          }
        });
        const unique = Object.values(profilesByName);
        setDiscountProfiles(
          unique.map((r) => ({ id: r.id, name: r.profileName, isDefaultProfile: r.isDefaultProfile }))
        );
        const defaultDiscount = (res.data || []).find((r) => r.isDefaultProfile === 1 || r.isDefaultProfile === true);
        if (defaultDiscount) {
          setSelections((s) => ({ ...s, discount: defaultDiscount.id }));
        }
      })
      .catch((e) => showHttpError(e));

    fetchCatalogs(mid)
      .then((res) => {
        if (!res.status) return;
        setCatalogProfiles(
          (res.data || []).map((c) => ({ id: c.id, name: c.catalogName, isDefaultProfile: c.isDefaultProfile }))
        );
      })
      .catch((e) => showHttpError(e));

    fetchBuyerInfoProfiles(mid)
      .then((res) => {
        if (res.error !== "0") return;
        setBuyerInfoProfilesFull(res.data || []); // store full for pills
        setBuyerInfoProfiles(
          (res.data || []).map((r) => ({ id: r.id, name: r.profileName, isDefaultProfile: r.isDefaultProfile }))
        );
        const defaultBuyerInfo = (res.data || []).find((r) => r.isDefaultProfile === 1);
        if (defaultBuyerInfo) {
          setSelections((s) => ({ ...s, buyerInfo: defaultBuyerInfo.id }));
        }
      })
      .catch((e) => showHttpError(e));

    fetchPaymentOptions(mid)
      .then((res) => {
        const rows = res.data || [];
        if (!rows.length) return;
        setPaymentOptionProfilesFull(rows);
        setPaymentOptionProfiles(
          rows.map((r) => ({ id: r.id, name: r.profileName, isDefaultProfile: r.isDefaultProfile }))
        );
        const defaultPaymentOption = rows.find((r) => r.isDefaultProfile === 1);
        if (defaultPaymentOption) {
          setSelections((s) => ({ ...s, paymentOption: defaultPaymentOption.id }));
        }
      })
      .catch((e) => showHttpError(e));

    fetchProcessingFees(mid)
      .then((res) => {
        const row = res.data || [];
        const flag = row?.isProcessingFeeEnabled;
        const presel =
          flag === "1" || flag === 1
            ? "1"
            : flag === "0" || flag === 0
            ? "0"
            : "1";
        setMerchantFeeDefault(presel);
      })
      .catch((e) => showHttpError(e));

    fetchRedirectProfiles(mid)
      .then((res) => {
        if (res.error !== "0") return;
        setRedirectProfilesFull(res.data || []); // store full for pills
        setRedirectProfiles(
          (res.data || []).map((r) => ({ id: r.id, name: r.templateName, isDefault: r.isDefault }))
        );
        const def = (res.data || []).find((r) => r.isDefault === 1);
        if (def) setSelections((s) => ({ ...s, redirect: def.id }));
      })
      .catch((e) => showHttpError(e));

    fetchRewardsCampaigns(mid)
      .then((res) => {
        if (!res?.status) return;
        const list = res.data || [];
        setRewardsCampaignsFull(list);
        setRewardsCampaigns(
          list.map((c) => ({ id: c.campaignId, name: c.campaignName }))
        );
      })
      .catch((e) => showHttpError(e));
  }, []);

  // ── Fetch ALL products on mount ────────────────────────────────
  useEffect(() => {
    const mid = getMerchantId();
    if (!mid) return;
    setProductsLoading(true);
    fetchAllProducts(mid)
      .then((res) => {
        if (!res.status) {
          setProducts([]);
          return;
        }
        const allProds = (res.data?.products || [])
          .filter((p) => (p.status || "").toUpperCase() === "ACTIVE")
          .map((p) => ({
          id: p.productId,
          name: p.name || "Unnamed",
          catalogId: null,
          catalogName: "",
          imageUrl: p.imageUrl || "",
          prices: (p.prices || []).map((pr) => {
            const defaultCur =
              (pr.currencies || []).find((c) => c.default) ||
              pr.currencies?.[0];
            return {
              id: String(pr.priceId),
              amount: defaultCur?.amount || 0,
              currency: defaultCur?.currency || "USD",
              priceType:
                pr.priceType === "recurring" ? "subscription" : "one-time",
              billingInterval: pr.intervalType || "monthly",
              variant: pr.metadata?.variant
                ? Object.entries(pr.metadata.variant)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(" / ")
                : "",
              currencies: pr.currencies || [],
              default: pr.default || false,
            };
          }),
        }));
        setProducts(allProds);
      })
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false));
  }, []);

  const handleProductChange = (pid) => {
    setSelectedProdId(pid);
    const prod = products.find((p) => p.id === pid);
    const oneTime = (prod?.prices || []).filter(
      (p) => p.priceType === "one-time" || p.priceType === "onetime"
    );
    const defaultPrice = oneTime.find((p) => p.default === true) || oneTime[0];
    setSelectedPriceId(defaultPrice ? defaultPrice.id : null);
    if (prod?.catalogId)
      setSelections((s) => ({ ...s, catalog: prod.catalogId }));
  };

  const selectedProduct = products.find(
    (p) => (p.id || p.productId) === selectedProdId
  );
  const selectedPrices = selectedProduct?.prices || [];
  const selectedPrice = selectedPrices.find((p) => p.id === selectedPriceId);

  const showMsg = (type, msg) => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "success" }), 3000);
  };

  // ── Generate ───────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!selectedProdId && !selections.catalog) {
      showMsg("error", "Please select a Product or Catalogue");
      return;
    }
    setLoading(true);
    try {
      const isProductMode = !!selectedProdId;
      const defaultBuyerId = buyerInfoProfilesFull.find(
        (p) => p.isDefaultProfile === 1 || p.isDefaultProfile === true
      )?.id;
      const defaultShippingId = shippingProfilesFull.find(
        (p) => p.isDefaultProfile === 1 || p.isDefaultProfile === true
      )?.id;
      const defaultDiscountId = discountProfilesFull.find(
        (p) => p.isDefaultProfile === 1 || p.isDefaultProfile === true
      )?.id;
      const effectiveDiscountId = selections.discount ?? 0;
      const discountProfileName =
        discountProfiles.find(
          (p) => String(p.id) === String(effectiveDiscountId)
        )?.name || "";
      const res = await savePaymentLink({
        merchantId: parseInt(getMerchantId()),
        paymentName: paymentName.trim(),
        paymentType: "CHECKOUT",
        ...(isProductMode
          ? { productId: selectedProdId, priceId: selectedPriceId || null }
          : { catalogId: selections.catalog || 0 }),
        shippingProfileId: selections.shipping ?? 0,
        buyerProfileId: selections.buyerInfo ?? 0,
        discountProfileName,
        billingId: selections.paymentOption ?? 0,
        isProcessingFeeApplied: selections.feeHandling ?? 0,
        redirectId: selections.redirect ?? 0,
      });
      if (res.error !== "0") {
        showMsg("error", res.message || "Failed to create payment");
        return;
      }
      setPaymentId(res.id);
      setGenerated(true);
      setActiveTab("link");
      let base = DEFAULT_BASE_URL;
      try {
        const brokerId = localStorage.getItem(`${SITENAMEALIAS}_broker_id`) || "";
        const infoRes = await fetch(
          `https://accounts.paybito.com/api/home/getBrokerWiseExchangeInfo?brokerId=${encodeURIComponent(brokerId)}`
        ).then((r) => r.json());
        base = buildCheckoutBase(infoRes && infoRes.domain);
      } catch (_) {
        base = DEFAULT_BASE_URL;
      }
      setCheckoutBaseUrl(base);
      setHtmlCode(getLinkFor(res.id, base));
      showMsg("success", res.message || "Payment link created!");
    } catch (e) {
      showMsg("error", "Failed to create payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getLinkFor = (id, baseOverride) => {
    const base = (baseOverride || checkoutBaseUrl).replace(/\/+$/, "");
    return `${base}/${encodeURIComponent(String(id))}`;
  };
  const getLink = () => getLinkFor(paymentId);
  const getBtnHtml = (sz) => {
    const w = { small: 80, medium: 105, large: 132 };
    const h = { small: 43, medium: 56, large: 70 };
    return `<a href="${getLink()}" target="_blank" style="display:inline-block;width:${
      w[sz]
    }px;height:${
      h[sz]
    }px;background:#6366f1;color:#fff;border-radius:6px;text-align:center;line-height:${
      h[sz]
    }px;font-size:${
      sz === "small" ? 11 : 13
    }px;font-weight:600;text-decoration:none;">Pay Now</a>`;
  };
  const getHyperlink = () =>
    `<a href="${getLink()}" target="_blank">Pay Now</a>`;
  const getQrHtml = () =>
    `<img src="https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
      getLink()
    )}&size=200x200&margin=10" alt="Pay Now QR Code" width="200" height="200" style="border-radius:8px;" />`;
  const copyText = (t) =>
    navigator.clipboard
      .writeText(t)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => showMsg("error", "Copy failed"));
  const handleTabAction = (tab) => {
    setActiveTab(tab);
    if (tab === "link") setHtmlCode(getLink());
    else if (tab === "hyperlink") setHtmlCode(getHyperlink());
    else if (tab === "button") setHtmlCode(getBtnHtml(buttonSize));
    else if (tab === "qr") setHtmlCode(getQrHtml());
  };
  const handleBtnSize = (sz) => {
    setButtonSize(sz);
    setHtmlCode(getBtnHtml(sz));
  };

  const handleSendEmail = async () => {
    const emails = emailList.split(",").map(e => e.trim()).filter(e => e);

    if (emails.length === 0) {
      showMsg("error", "Please enter at least one valid email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    if (invalidEmails.length > 0) {
      showMsg("error", `Invalid email format: ${invalidEmails.join(", ")}`);
      return;
    }

    // Duplicate check (case-insensitive)
    const seen = new Set();
    const duplicates = [];
    emails.forEach((e) => {
      const k = e.toLowerCase();
      if (seen.has(k)) duplicates.push(e);
      else seen.add(k);
    });
    if (duplicates.length > 0) {
      showMsg("error", `Duplicate email not allowed: ${[...new Set(duplicates)].join(", ")}`);
      return;
    }

    setSendingEmail(true);
    try {
      const brokerId = localStorage.getItem(`${SITENAMEALIAS}_broker_id`) || "";
      const response = await apiPost(`${API_BASE}/payment/send-payment-link`, {
        merchantId: String(getMerchantId()),
        emails,
        paymentLink: getLink(),
        paymentOrderId: String(paymentId),
        brokerId,
      });

      if (response.status === true) {
        const { totalSent = 0, totalFailed = 0, results = [] } = response.data || {};
        if (totalFailed > 0) {
          const failed = results.filter((r) => r.status !== "SENT");
          const detail = failed.map((r) => `${r.email}${r.errorDetail ? ` (${r.errorDetail})` : ""}`).join(", ");
          showMsg("error", `Sent ${totalSent}, failed ${totalFailed}: ${detail}`);
        } else {
          showMsg("success", response.message || `Email sent successfully to ${totalSent} recipient(s)`);
          setEmailList("");
        }
      } else {
        showMsg("error", response.message || "Failed to send emails");
      }
    } catch (error) {
      console.error("Send email error:", error);
      showMsg("error", "Failed to send emails. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  const isSubscriptionProduct = selectedPrice?.priceType === "subscription";
  const exchangeName =
    localStorage.getItem(SITENAMEALIAS + "_exchange") || "Brand";

  const paymentOptionDefaultTags = [
    ...(hasStripe
      ? [
          {
            label: "Card (PayBito Apex)",
            icon: "mdi-credit-card-outline",
            bg: "rgba(99,102,241,.10)",
            color: "#6366f1",
            border: "rgba(99,102,241,.22)",
          },
        ]
      : []),
    ...(hasPayPal
      ? [
          {
            label: "PayBito Titan",
            icon: "mdi-alpha-p-circle-outline",
            bg: "rgba(245,158,11,.10)",
            color: "#f59e0b",
            border: "rgba(245,158,11,.22)",
          },
        ]
      : []),
    ...(hasKurvPay
      ? [
          {
            label: "PayBito Nexus",
            icon: "mdi-credit-card-outline",
            bg: "rgba(99,102,241,.10)",
            color: "#6366f1",
            border: "rgba(99,102,241,.22)",
          },
        ]
      : []),
    ...(hasNetBilling
      ? [
          {
            label: "PayBito Vertex",
            icon: "mdi-credit-card-outline",
            bg: "rgba(99,102,241,.10)",
            color: "#6366f1",
            border: "rgba(99,102,241,.22)",
          },
        ]
      : []),
    ...(hasHMS
      ? [
          {
            label: "PayBito Nova",
            icon: "mdi-credit-card-outline",
            bg: "rgba(99,102,241,.10)",
            color: "#6366f1",
            border: "rgba(99,102,241,.22)",
          },
        ]
      : []),
    ...(hasNMI
      ? [
          {
            label: "PayBito Zenith",
            icon: "mdi-flash",
            bg: "rgba(124,58,237,.10)",
            color: "#7c3aed",
            border: "rgba(124,58,237,.22)",
          },
        ]
      : []),
    {
      label: "Crypto - Brand Wallet",
      icon: "mdi-wallet-outline",
      bg: "rgba(16,185,129,.10)",
      color: "#10b981",
      border: "rgba(16,185,129,.22)",
    },
    {
      label: "Crypto - External Wallet",
      icon: "mdi-link-variant",
      bg: "rgba(20,184,166,.10)",
      color: "#14b8a6",
      border: "rgba(20,184,166,.22)",
    },
    {
      label: "Crypto - Guest Checkout",
      icon: "mdi-account-outline",
      bg: "rgba(139,92,246,.10)",
      color: "#8b5cf6",
      border: "rgba(139,92,246,.22)",
    },
  ];

  const paymentOptionDefaultTagsRecurring = [
    ...(hasStripe
      ? [
          {
            label: "Card (PayBito Apex)",
            icon: "mdi-credit-card-outline",
            bg: "rgba(99,102,241,.10)",
            color: "#6366f1",
            border: "rgba(99,102,241,.22)",
          },
        ]
      : []),
    ...(hasPayPal
      ? [
          {
            label: "PayBito Titan",
            icon: "mdi-alpha-p-circle-outline",
            bg: "rgba(245,158,11,.10)",
            color: "#f59e0b",
            border: "rgba(245,158,11,.22)",
          },
        ]
      : []),
    ...(hasKurvPay
      ? [
          {
            label: "PayBito Nexus",
            icon: "mdi-credit-card-outline",
            bg: "rgba(99,102,241,.10)",
            color: "#6366f1",
            border: "rgba(99,102,241,.22)",
          },
        ]
      : []),
    ...(hasNetBilling
      ? [
          {
            label: "PayBito Vertex",
            icon: "mdi-credit-card-outline",
            bg: "rgba(99,102,241,.10)",
            color: "#6366f1",
            border: "rgba(99,102,241,.22)",
          },
        ]
      : []),
    ...(hasHMS
      ? [
          {
            label: "PayBito Nova",
            icon: "mdi-credit-card-outline",
            bg: "rgba(99,102,241,.10)",
            color: "#6366f1",
            border: "rgba(99,102,241,.22)",
          },
        ]
      : []),
    ...(hasNMI
      ? [
          {
            label: "PayBito Zenith",
            icon: "mdi-flash",
            bg: "rgba(124,58,237,.10)",
            color: "#7c3aed",
            border: "rgba(124,58,237,.22)",
          },
        ]
      : []),
    {
      label: "Crypto - Brand Wallet",
      icon: "mdi-wallet-outline",
      bg: "rgba(16,185,129,.10)",
      color: "#10b981",
      border: "rgba(16,185,129,.22)",
    },
  ];

  const activePaymentDefaultTags = isSubscriptionProduct
    ? paymentOptionDefaultTagsRecurring
    : paymentOptionDefaultTags;

  // ── Payment option change — validates pmIds for subscription products ──
  const handlePaymentOptionChange = (v) => {
    setSelections((s) => ({ ...s, paymentOption: v }));
  };

  // ── Build selectedDetailTags for each row ──────────────────────
  const getSelectedDetailTags = (key) => {
    const val = selections[key];
    if (!val) return undefined;
    switch (key) {
      case "paymentOption":
        return buildPaymentOptionTags(val, paymentOptionProfilesFull, isSubscriptionProduct);
      case "buyerInfo":
        return buildBuyerInfoTags(val, buyerInfoProfilesFull);
      case "shipping":
        return buildShippingTags(val, shippingProfilesFull);
      case "discount":
        return buildDiscountTags(val, discountProfilesFull);
      case "redirect":
        return buildRedirectTags(val, redirectProfilesFull);
      case "rewards":
        return buildRewardsTags(val, rewardsCampaignsFull);
      default:
        return undefined;
    }
  };

  // ── Profiles shown in the Configuration summary (right panel) ──
  const PROFILE_CONFIGS = [
    {
      key: "catalog",
      label: "Product Catalogue",
      options: catalogProfiles,
      color: "var(--btn-color)",
      icon: "mdi-package-variant",
      optional: false,
    },
    {
      key: "paymentOption",
      label: "Payment Options",
      options: paymentOptionProfiles,
      color: "#6366f1",
      icon: "mdi-link-variant",
      optional: true,
    },
    {
      key: "feeHandling",
      label: "Fee Handling",
      options: getFeeOptions(),
      color: "#ec4899",
      icon: "mdi-percent-outline",
      optional: true,
    },
    {
      key: "buyerInfo",
      label: "Buyer Information",
      options: buyerInfoProfiles,
      color: "#8b5cf6",
      icon: "mdi-account-check-outline",
      optional: true,
    },
    {
      key: "shipping",
      label: "Shipping",
      options: shippingProfiles,
      color: "#3b82f6",
      icon: "mdi-truck-delivery-outline",
      optional: true,
    },
    {
      key: "discount",
      label: "Discounts",
      options: discountProfiles,
      color: "#22c55e",
      icon: "mdi-tag-multiple-outline",
      optional: true,
    },
    {
      key: "redirect",
      label: "Redirects",
      options: redirectProfiles,
      color: "#14b8a6",
      icon: "mdi-redo-variant",
      optional: true,
    },
  ];

  // Inside your component, after the state declarations and before the return statement
  const profileConfigs = [
    {
      key: "paymentOption",
      label: "Payment Options",
      options: paymentOptionProfiles,
      color: "#6366f1",
      icon: "mdi-link-variant",
      optional: true,
    },
    {
      key: "feeHandling",
      label: "Fee Handling",
      options: getFeeOptions(),
      color: "#ec4899",
      icon: "mdi-percent-outline",
      optional: true,
    },
    {
      key: "buyerInfo",
      label: "Buyer Information",
      options: buyerInfoProfiles,
      color: "#8b5cf6",
      icon: "mdi-account-check-outline",
      optional: true,
    },
    {
      key: "shipping",
      label: "Shipping",
      options: shippingProfiles,
      color: "#3b82f6",
      icon: "mdi-truck-delivery-outline",
      optional: true,
    },
    {
      key: "discount",
      label: "Discounts",
      options: discountProfiles,
      color: "#22c55e",
      icon: "mdi-tag-multiple-outline",
      optional: true,
    },
    {
      key: "redirect",
      label: "Redirects",
      options: redirectProfiles,
      color: "#14b8a6",
      icon: "mdi-redo-variant",
      optional: true,
    },
  ];

  // ── Customize Checkout rows ────────────────────────────────────
  const CUSTOMIZE_ROWS = [
    {
      key: "paymentOption",
      label: "Payment Options",
      options: paymentOptionProfiles,
      color: "#6366f1",
      optional: true,
      redirectPath: "/billing",
      btnLabel: "Add Payment Option",
      defaultTags: isSubscriptionProduct
        ? paymentOptionDefaultTagsRecurring
        : paymentOptionDefaultTags,
    },
    {
      key: "feeHandling",
      label: "Fee Handling",
      options: getFeeOptions(),
      color: "#ec4899",
      optional: true,
      redirectPath: "/fees-handling",
      btnLabel: "Configure Fees",
    },
    {
      key: "buyerInfo",
      label: "Buyer Info",
      options: buyerInfoProfiles,
      color: "#8b5cf6",
      optional: true,
      redirectPath: "/buyer-information",
      btnLabel: "Add Buyer Info",
      defaultTags: [
        {
          label: "Collect Email",
          icon: "mdi-email-outline",
          bg: "rgba(139,92,246,.10)",
          color: "#8b5cf6",
          border: "rgba(139,92,246,.22)",
        },
      ],
    },
    {
      key: "shipping",
      label: "Shipping",
      options: shippingProfiles,
      color: "#3b82f6",
      optional: true,
      redirectPath: "/shipping-handling",
      btnLabel: "Add Shipping",
      defaultTags: [
        {
          label: "No Shipping Rate",
          icon: "mdi-truck-delivery-outline",
          bg: "rgba(59,130,246,.10)",
          color: "#3b82f6",
          border: "rgba(59,130,246,.22)",
        },
        {
          label: "No Tax Rate",
          icon: "mdi-receipt-outline",
          bg: "rgba(59,130,246,.08)",
          color: "#60a5fa",
          border: "rgba(59,130,246,.18)",
        },
      ],
    },
    {
      key: "discount",
      label: "Discounts",
      options: discountProfiles,
      color: "#22c55e",
      optional: true,
      redirectPath: "/discounting-profile",
      btnLabel: "Add Discount",
      defaultTags: [
        {
          label: "No Discounts",
          icon: "mdi-tag-off-outline",
          bg: "rgba(34,197,94,.10)",
          color: "#22c55e",
          border: "rgba(34,197,94,.22)",
        },
      ],
    },
    // {
    //   key: "rewards",
    //   label: "Rewards",
    //   options: rewardsCampaigns,
    //   color: "#eab308",
    //   optional: true,
    //   redirectPath: "/rewards",
    //   btnLabel: "Add Rewards",
    //   defaultTags: [
    //     {
    //       label: "No Campaign",
    //       icon: "mdi-gift-off-outline",
    //       bg: "rgba(234,179,8,.10)",
    //       color: "#eab308",
    //       border: "rgba(234,179,8,.22)",
    //     },
    //   ],
    // },
    {
      key: "redirect",
      label: "Redirects",
      options: redirectProfiles,
      color: "#14b8a6",
      optional: true,
      redirectPath: "/redirects",
      btnLabel: "Add Redirect",
      defaultTags: [
        {
          label: "Success URL —",
          icon: "mdi-check-circle-outline",
          bg: "rgba(16,185,129,.10)",
          color: "#10b981",
          border: "rgba(16,185,129,.22)",
        },
        {
          label: "Failure URL —",
          icon: "mdi-close-circle-outline",
          bg: "rgba(239,68,68,.10)",
          color: "#ef4444",
          border: "rgba(239,68,68,.22)",
        },
      ],
    },
  ];

  const isProductMode = !!selectedProdId;
  const canGenerate = isProductMode ? !!selectedPriceId : !!selections.catalog;
  const feeHandlingDefaultLabel = getFeeHandlingDisplayLabel(
    selections.feeHandling,
    isProcessingFeeEnabled
  );

  return (
    <Fragment>
      <style>{`
        @keyframes slideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        .cp-sel:focus{border-color:var(--btn-color)!important;box-shadow:0 0 0 3px rgba(99,102,241,.14)!important;outline:none;}
        .cp-inp:focus{border-color:var(--btn-color)!important;box-shadow:0 0 0 3px rgba(99,102,241,.14)!important;outline:none;}
        .cp-tab:hover{opacity:1!important;}
        .cp-coll{overflow:hidden;transition:max-height .35s ease;}
        @keyframes dropFade{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
        .cp-grid>*{min-width:0;overflow:hidden;}
        @media(max-width:860px){.cp-grid{grid-template-columns:1fr!important;}.cp-right{position:static!important;}.cp-bar{flex-direction:column!important;gap:8px!important;}.cp-bar-btn{width:100%!important;justify-content:center!important;}}
        @media(max-width:576px){.cp-page-header{text-align:center!important;}.cp-page-wrapper{padding:24px 16px 100px!important;}}
        @media(max-width:520px){
          .cp-subrow{flex-direction:column!important;gap:6px!important;}
          .cp-subrow-label{min-width:unset!important;width:100%!important;padding-top:0!important;padding-bottom:2px!important;}
          .cp-subrow-right{width:100%!important;min-width:0!important;}
          .cp-subrow-dropdown{justify-content:flex-start!important;}
          .cp-subrow-sel{min-width:unset!important;width:100%!important;flex:1 1 0!important;}
          .cp-price-sel{width:100%!important;min-width:unset!important;}
        }
      `}</style>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="cp-page-wrapper" style={{ padding: "32px 24px 90px" }}>
            <Toast msg={toast.msg} type={toast.type} />

            {/* Page header */}
            <div
              className="cp-page-header"
              style={{
                marginBottom: "24px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: "22px",
                    fontWeight: "700",
                    color: "var(--text-color)",
                    margin: 0,
                    letterSpacing: "-.3px",
                  }}
                >
                  Create Payment
                </h2>
                <p
                  style={{
                    fontSize: "13px",
                    color: "var(--text-color)",
                    opacity: 0.45,
                    margin: "6px 0 0",
                  }}
                >
                  Assemble your profiles to generate a shareable payment link
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  window.location.href = "/view-payments";
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: "1px solid var(--body-color-less2)",
                  background: "var(--body-color-less)",
                  color: "var(--text-color)",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all .15s ease",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="mdi mdi-format-list-bulleted" style={{ fontSize: 16 }} />
                View Payment Links
              </button>
            </div>

            <div
              className="cp-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,60fr) minmax(0,40fr)",
                gap: "20px",
                alignItems: "start",
                gridAutoRows: "auto",
              }}
            >
              {/* ── LEFT ── */}
              <div style={{ minWidth: 0, width: "100%", overflow: "visible" }}>
                {/* Card 1 — Product + Catalogue picker */}
                <div style={CARD}>
                  <SectionLabel
                    icon="mdi-package-variant"
                    color="var(--btn-color)"
                    text="What are you selling?"
                  />

                  {productsLoading ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                        color: "var(--text-color)",
                        opacity: 0.45,
                      }}
                    >
                      <i
                        className="mdi mdi-loading mdi-spin"
                        style={{ fontSize: 15 }}
                      />{" "}
                      Loading products…
                    </div>
                  ) : catalogProfiles.length === 0 && products.length === 0 ? (
                    <EmptyProfileRow
                      label="Products"
                      color="var(--btn-color)"
                      redirectPath="/product-catalog"
                      btnLabel="Create Products"
                    />
                  ) : (
                    <ProductCatalogPicker
                      products={products}
                      catalogs={catalogProfiles}
                      selectedId={selectedProdId}
                      selectedCatalogId={selections.catalog}
                      onSelect={handleProductChange}
                      onCatalogSelect={(catId) => {
                        setSelections((s) => ({ ...s, catalog: catId }));
                        setSelectedProdId(null);
                        setSelectedPriceId(null);
                      }}
                      isReadOnly={isReadOnly}
                    />
                  )}

                  {/* Price */}
                  {(selectedProduct || selections.catalog) && (
                    <div
                      style={{
                        marginTop: 16,
                        paddingTop: 16,
                        borderTop: "1px solid var(--body-color-less2)",
                      }}
                    >
                      <label style={FIELD_LABEL}>Price</label>
                      {!selectedProduct ? (
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--text-color)",
                            opacity: 0.4,
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <i
                            className="mdi mdi-information-outline"
                            style={{ fontSize: 13 }}
                          />{" "}
                          Select a product to see pricing options
                        </div>
                      ) : selectedPrices.length === 0 ? (
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--text-color)",
                            opacity: 0.4,
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <i
                            className="mdi mdi-alert-outline"
                            style={{ fontSize: 13 }}
                          />{" "}
                          No one-time prices set for this product
                        </div>
                      ) : (
                        <>
                          <select
                            className="cp-price-sel"
                            value={selectedPriceId || ""}
                            onChange={(e) =>
                              setSelectedPriceId(
                                e.target.value ? e.target.value : null
                              )
                            }
                            style={{
                              width: "auto",
                              minWidth: 200,
                              height: 40,
                              padding: "0 12px",
                              fontSize: 14,
                              borderRadius: 8,
                              border: "1.5px solid var(--body-color-less2)",
                              background: "var(--body-color)",
                              color: "var(--text-color)",
                              outline: "none",
                              cursor: "pointer",
                              appearance: "auto",
                            }}
                          >
                            <option value="">— Select price —</option>
                            {selectedPrices.map((p) => (
                              <option key={p.id} value={p.id}>
                                {formatPrice(p)}
                                {p.variant ? ` — ${p.variant}` : ""}
                              </option>
                            ))}
                          </select>

                          {selectedPrice &&
                            (selectedPrice.currencies || []).length > 0 && (
                              <div
                                style={{
                                  marginTop: 10,
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 6,
                                }}
                              >
                                {(selectedPrice.currencies || []).map(
                                  (c, i) => (
                                    <span
                                      key={i}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 5,
                                        padding: "4px 12px",
                                        borderRadius: 20,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        background:
                                          "color-mix(in srgb,var(--btn-color) 10%,transparent)",
                                        border:
                                          "1px solid color-mix(in srgb,var(--btn-color) 22%,transparent)",
                                        color: "var(--btn-color)",
                                      }}
                                    >
                                      <span
                                        style={{ opacity: 0.6, fontSize: 11 }}
                                      >
                                        {c.currency}
                                      </span>
                                      <span>
                                        {parseFloat(c.amount).toLocaleString()}
                                      </span>
                                    </span>
                                  )
                                )}
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Card 2 — Payment Name */}
                <div style={CARD}>
                  <SectionLabel
                    icon="mdi-text-box-outline"
                    color="#6366f1"
                    text="Payment Name"
                    optional
                  />
                  <input
                    className="cp-inp"
                    type="text"
                    placeholder="e.g. Summer Sale Checkout"
                    value={paymentName}
                    onChange={(e) => setPaymentName(e.target.value)}
                    style={{
                      width: "100%",
                      height: 40,
                      padding: "0 14px",
                      fontSize: 14,
                      borderRadius: 8,
                      border: "1.5px solid var(--body-color-less2)",
                      background: "var(--body-color)",
                      color: "var(--text-color)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* Card 3 — Customize Checkout */}
                <div style={CARD}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <SectionLabel
                      icon="mdi-tune-variant"
                      color="#8b5cf6"
                      text="Customize Checkout"
                      optional
                      noMargin
                    />
                    <button
                      type="button"
                      disabled={isReadOnly}
                      onClick={() => {
                        if (isReadOnly) {
                          showToast("error", "You need write access to perform this operation");
                          return;
                        }
                        setCustomizeOpen((o) => !o);
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "5px 12px",
                        borderRadius: 8,
                        border:
                          "1.5px solid color-mix(in srgb,#8b5cf6 35%,transparent)",
                        background: customizeOpen
                          ? "color-mix(in srgb,#8b5cf6 12%,transparent)"
                          : "transparent",
                        color: "#a78bfa",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: isReadOnly ? "not-allowed" : "pointer",
                        opacity: isReadOnly ? 0.5 : 1,
                        transition: "all .15s",
                      }}
                    >
                      <i
                        className={`mdi ${
                          customizeOpen ? "mdi-chevron-up" : "mdi-tune-variant"
                        }`}
                        style={{ fontSize: 13 }}
                      />
                      {customizeOpen ? "Hide options" : "Customise"}
                    </button>
                  </div>

                  <div
                    className="cp-coll"
                    style={{ maxHeight: customizeOpen ? "900px" : "0" }}
                  >
                    <div style={{ height: 14 }} />
                    {CUSTOMIZE_ROWS.map((row) => (
                      <SubRow
                        key={row.key}
                        label={row.label}
                        color={row.color}
                        options={row.options}
                        value={selections[row.key]}
                        onChange={
                          row.key === "paymentOption"
                            ? handlePaymentOptionChange
                            : row.key === "feeHandling"
                            ? handleFeeHandlingChange
                            : (v) =>
                                setSelections((s) => ({ ...s, [row.key]: v }))
                        }
                        optional={row.optional}
                        redirectPath={row.redirectPath}
                        btnLabel={row.btnLabel}
                        defaultTags={row.defaultTags}
                        feeHandlingDefault={
                          row.key === "feeHandling"
                            ? feeHandlingDefaultLabel
                            : undefined
                        }
                        selectedDetailTags={
                          row.key !== "feeHandling"
                            ? getSelectedDetailTags(row.key)
                            : undefined
                        }
                        useRedirectIcon={row.key === "feeHandling"}
                        showUserDefaultLabel={true}
                        userDefaultProperty={row.key === "feeHandling" ? "isProcessingFeeEnabled" : "isDefault"}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* ── RIGHT (sticky) ── */}
              <div
                className="cp-right"
                style={{
                  position: "sticky",
                  top: 24,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  minWidth: 0,
                  width: "100%",
                }}
              >
                {/* Configuration summary */}
                <div style={RIGHT_CARD}>
                  <div style={RIGHT_LABEL}>Configuration</div>
                  {profileConfigs.map((cfg) => {
                    const sel = cfg.options.find(
                      (o) => String(o.id) === String(selections[cfg.key])
                    );
                    return (
                      <div
                        key={cfg.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 0",
                        }}
                      >
                        <i
                          className={
                            sel
                              ? "mdi mdi-check-circle"
                              : "mdi mdi-circle-outline"
                          }
                          style={{
                            fontSize: 15,
                            color: sel ? "#10b981" : "var(--text-color)",
                            opacity: sel ? 1 : 0.2,
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            color: "var(--text-color)",
                            opacity: sel ? 1 : 0.4,
                            flex: 1,
                          }}
                        >
                          {cfg.label}
                        </span>
                        {sel && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: cfg.color,
                              background: `color-mix(in srgb,${cfg.color} 12%,transparent)`,
                              borderRadius: 5,
                              padding: "2px 7px",
                              maxWidth: 110,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sel.name}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Product section */}
                  {selectedProduct && (
                    <div
                      style={{
                        borderTop: "1px solid var(--body-color-less2)",
                        marginTop: 6,
                        paddingTop: 6,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 0",
                        }}
                      >
                        <i
                          className="mdi mdi-check-circle"
                          style={{
                            fontSize: 15,
                            color: "#10b981",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            color: "var(--text-color)",
                            flex: 1,
                          }}
                        >
                          Product
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "var(--btn-color)",
                            background:
                              "color-mix(in srgb,var(--btn-color) 12%,transparent)",
                            borderRadius: 5,
                            padding: "2px 7px",
                            maxWidth: 110,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {selectedProduct.name}
                        </span>
                      </div>
                      {selectedPrice && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 0",
                          }}
                        >
                          <i
                            className="mdi mdi-check-circle"
                            style={{
                              fontSize: 15,
                              color: "#10b981",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 13,
                              color: "var(--text-color)",
                              flex: 1,
                            }}
                          >
                            Price
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#f59e0b",
                              background: "rgba(245,158,11,.1)",
                              borderRadius: 5,
                              padding: "2px 7px",
                            }}
                          >
                            {formatPrice(selectedPrice)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment Name section */}
                  {paymentName.trim() && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 0",
                        borderTop: "1px solid var(--body-color-less2)",
                        marginTop: 6,
                      }}
                    >
                      <i
                        className="mdi mdi-check-circle"
                        style={{
                          fontSize: 15,
                          color: "#10b981",
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          color: "var(--text-color)",
                          flex: 1,
                        }}
                      >
                        Payment Name
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#6366f1",
                          background: "rgba(99,102,241,.1)",
                          borderRadius: 5,
                          padding: "2px 7px",
                          maxWidth: 110,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {paymentName}
                      </span>
                    </div>
                  )}
                </div>

                {/* Output panel */}
                {generated && (
                  <div style={{ ...RIGHT_CARD, animation: "fadeIn .3s ease" }}>
                    {/* Payment Link Created header */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <i className="mdi mdi-check-circle" style={{ fontSize: 15, color: "#10b981" }} />
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#10b981" }}>Payment Link Created</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.45 }}>ID: {paymentId}</span>
                        <button
                          onClick={() => { copyText(paymentId); setCopiedId(true); setTimeout(() => setCopiedId(false), 2000); }}
                          title="Copy ID"
                          style={{
                            width: 26, height: 26, borderRadius: 6, border: "none",
                            background: copiedId ? "#10b981" : "var(--btn-color)",
                            color: "#fff", fontSize: 12, cursor: "pointer",
                            display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all .2s",
                          }}
                        >
                          <i className={`mdi ${copiedId ? "mdi-check" : "mdi-content-copy"}`} style={{ fontSize: 12 }} />
                        </button>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div
                      style={{
                        display: "flex",
                        background: "var(--body-color)",
                        borderRadius: 9,
                        padding: 3,
                        border: "1px solid var(--body-color-less2)",
                        gap: 3,
                        marginBottom: 12,
                      }}
                    >
                      {[
                        ["link", "mdi-link-variant", "Link"],
                        ["button", "mdi-gesture-tap-button", "Button"],
                        ["hyperlink", "mdi-link", "Hyperlink"],
                        ["qr", "mdi-qrcode", "QR"],
                      ].map(([k, ic, lb]) => (
                        <button
                          key={k}
                          className="cp-tab"
                          type="button"
                          onClick={() => handleTabAction(k)}
                          style={{
                            flex: 1, padding: "7px 4px", borderRadius: 7, border: "none",
                            background: activeTab === k ? "var(--btn-color)" : "transparent",
                            color: activeTab === k ? "#fff" : "var(--text-color)",
                            fontSize: 11, fontWeight: 600, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                            opacity: activeTab === k ? 1 : 0.55, transition: "all .15s",
                          }}
                        >
                          <i className={`mdi ${ic}`} style={{ fontSize: 13 }} />
                          {lb}
                        </button>
                      ))}
                    </div>

                    {activeTab === "button" && (
                      <div style={{ marginBottom: 10, display: "flex", gap: 6 }}>
                        {[["small", "80×43"], ["medium", "105×56"], ["large", "132×70"]].map(([sz, dm]) => (
                          <button key={sz} type="button" onClick={() => handleBtnSize(sz)}
                            style={{
                              flex: 1, padding: "5px 8px", borderRadius: 7,
                              border: buttonSize === sz ? "1.5px solid var(--btn-color)" : "1.5px solid var(--body-color-less2)",
                              background: buttonSize === sz ? "color-mix(in srgb,var(--btn-color) 10%,transparent)" : "var(--body-color)",
                              color: buttonSize === sz ? "var(--btn-color)" : "var(--text-color)",
                              fontSize: 11, fontWeight: 600, cursor: "pointer",
                            }}
                          >
                            {dm}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Embed / Code area */}
                    {activeTab === "qr" ? (
                      <div style={{ textAlign: "center", paddingBottom: 4 }}>
                        <SimpleQR value={getLink()} size={140} />
                        <div style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.4, marginTop: 8 }}>Scan to pay</div>
                        <button onClick={() => copyText(getQrHtml())}
                          style={{ marginTop: 10, width: "100%", padding: 7, borderRadius: 7, border: "1px solid var(--btn-color)", background: "transparent", color: "var(--btn-color)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          <i className="mdi mdi-content-copy" /> Copy Embed Code
                        </button>
                      </div>
                    ) : (
                      <>
                        <textarea ref={textareaRef} readOnly value={htmlCode}
                          style={{
                            width: "100%", minHeight: 80, padding: "10px 12px", borderRadius: 8,
                            border: "1px solid var(--body-color-less2)", background: "var(--body-color)",
                            color: "var(--text-color)", fontSize: 11, fontFamily: "'SF Mono',monospace",
                            resize: "vertical", outline: "none", boxSizing: "border-box",
                          }}
                        />
                        <button onClick={() => copyText(htmlCode)}
                          style={{ marginTop: 8, width: "100%", padding: 7, borderRadius: 7, border: "1px solid var(--btn-color)", background: "transparent", color: "var(--btn-color)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          <i className="mdi mdi-content-copy" /> Copy Code
                        </button>
                      </>
                    )}

                    {/* Preview */}
                    <div style={{ marginTop: 12, borderTop: "1px solid var(--body-color-less2)", paddingTop: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--text-color)", opacity: 0.4, marginBottom: 8 }}>Preview</div>
                      <div style={{ padding: 14, borderRadius: 8, border: "1px dashed var(--body-color-less2)", background: "var(--body-color)", textAlign: "center", minHeight: 52, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {activeTab === "link" && <a href={getLink()} target="_blank" rel="noreferrer" style={{ color: "#818cf8", fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>{getLink()}</a>}
                        {activeTab === "button" && (
                          <a href={getLink()} target="_blank" rel="noreferrer"
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: buttonSize === "small" ? 80 : buttonSize === "large" ? 132 : 105, height: buttonSize === "small" ? 43 : buttonSize === "large" ? 70 : 56, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", borderRadius: 6, fontWeight: 700, fontSize: buttonSize === "small" ? 11 : 13, textDecoration: "none" }}
                          >Pay Now</a>
                        )}
                        {activeTab === "hyperlink" && <a href={getLink()} target="_blank" rel="noreferrer" style={{ color: "var(--btn-color)", fontWeight: 600, fontSize: 14 }}>Pay Now</a>}
                        {activeTab === "qr" && <SimpleQR value={getLink()} size={100} />}
                      </div>
                    </div>

                    {/* Link copy row — below preview */}
                    <div style={{ marginTop: 12, borderTop: "1px solid var(--body-color-less2)", paddingTop: 12, display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8, border: "1px solid var(--body-color-less2)", background: "var(--body-color)" }}>
                      <span style={{ flex: 1, fontSize: 11, fontFamily: "monospace", color: "#818cf8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {getLink()}
                      </span>
                      <button onClick={() => copyText(getLink())} title="Copy link"
                        style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 6, border: "none", background: copied ? "#10b981" : "var(--btn-color)", color: "#fff", fontSize: 14, cursor: "pointer", transition: "all .2s", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <i className={`mdi ${copied ? "mdi-check" : "mdi-content-copy"}`} />
                      </button>
                      <button onClick={() => window.open(getLink(), "_blank")} title="Open link"
                        style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 6, border: "none", background: "var(--btn-color)", color: "#fff", fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all .2s" }}
                      >
                        <i className="mdi mdi-open-in-new" />
                      </button>
                    </div>

                    {/* Send Email Section */}
                    <div style={{
                      marginTop: 16,
                      borderTop: "1px solid var(--body-color-less2)",
                      paddingTop: 16,
                      marginBottom: 20 // Added margin bottom for better spacing on mobile
                    }}>
                      <div style={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: ".6px",
                        color: "var(--text-color)",
                        opacity: 0.4,
                        marginBottom: 12
                      }}>
                        Send Payment Link via Email
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <textarea
                          placeholder="Enter emails with comma separation (e.g., customer1@example.com, customer2@example.com)"
                          value={emailList}
                          onChange={(e) => setEmailList(e.target.value)}
                          style={{
                            width: "100%",
                            minHeight: 80,
                            padding: "10px 12px",
                            borderRadius: 8,
                            border: "1px solid var(--body-color-less2)",
                            background: "var(--body-color)",
                            color: "var(--text-color)",
                            fontSize: 12,
                            fontFamily: "monospace",
                            resize: "vertical",
                            outline: "none",
                            boxSizing: "border-box",
                          }}
                        />
                        <button
                          onClick={handleSendEmail}
                          disabled={!emailList.trim() || sendingEmail}
                          style={{
                            width: "100%",
                            padding: "12px 16px", // Increased padding for better touch target on mobile
                            borderRadius: 8,
                            border: "none",
                            background: emailList.trim() && !sendingEmail ? "var(--btn-color)" : "var(--body-color-less2)",
                            color: emailList.trim() && !sendingEmail ? "#fff" : "var(--text-color)",
                            opacity: emailList.trim() && !sendingEmail ? 1 : 0.5,
                            fontSize: 14, // Slightly larger font for mobile
                            fontWeight: 600,
                            cursor: emailList.trim() && !sendingEmail ? "pointer" : "not-allowed",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            transition: "all .2s",
                            minHeight: 44, // Minimum touch target size for mobile
                          }}
                        >
                          {sendingEmail ? (
                            <>
                              <i className="mdi mdi-loading mdi-spin" style={{ fontSize: 16 }} /> Sending...
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-send" style={{ fontSize: 16 }} /> Send Email
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pre-generate placeholder */}
                {!generated && (
                  <div
                    style={{
                      ...RIGHT_CARD,
                      textAlign: "center",
                      padding: "28px 18px",
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background:
                          "color-mix(in srgb,var(--btn-color) 10%,transparent)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px",
                        fontSize: 20,
                        color: "var(--btn-color)",
                      }}
                    >
                      <i className="mdi mdi-auto-fix" />
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-color)",
                        marginBottom: 5,
                      }}
                    >
                      Ready to generate
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--text-color)",
                        opacity: 0.4,
                        lineHeight: 1.6,
                      }}
                    >
                      Configure your checkout options above, then use the button below to generate your payment link.
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        justifyContent: "center",
                        marginTop: 14,
                      }}
                    >
                      {["Link", "Button", "QR Code", "Hyperlink"].map((f) => (
                        <span
                          key={f}
                          style={{
                            padding: "3px 10px",
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 600,
                            background:
                              "color-mix(in srgb,var(--btn-color) 8%,transparent)",
                            color: "var(--btn-color)",
                            border:
                              "1px solid color-mix(in srgb,var(--btn-color) 15%,transparent)",
                          }}
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* Sticky CTA */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "var(--body-color-less)",
          borderTop: "1px solid var(--body-color-less2)",
          padding: "12px 24px",
          zIndex: 9,
          backdropFilter: "blur(8px)",
        }}
      >
        <div
          className="cp-bar"
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "minmax(0,60fr) minmax(0,40fr)",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {/* Left cell — empty spacer, mirrors left column */}
          <div />

          {/* Right cell — buttons sit inside the same width as the right panel */}
          {/* Right cell — ALL buttons right-aligned, matching right panel width */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            {/* Main Create/Regenerate Button */}
            <button
              className="cp-bar-btn"
              disabled={!canGenerate || loading || isReadOnly}
              onClick={() => {
                if (isReadOnly) {
                  showToast("error", "You need write access to perform this operation");
                  return;
                }
                handleGenerate();
              }}
              style={{
                padding: "10px 16px",
                borderRadius: 10,
                border: "none",
                background: canGenerate && !isReadOnly
                  ? "var(--btn-color)"
                  : "var(--body-color-less2)",
                color: canGenerate && !isReadOnly ? "#fff" : "var(--text-color)",
                opacity: isReadOnly ? 0.5 : (canGenerate ? (loading ? 0.7 : 1) : 0.5),
                fontSize: 12,
                fontWeight: 600,
                cursor: canGenerate && !loading && !isReadOnly ? "pointer" : "not-allowed",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                transition: "all .2s",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {loading ? (
                <>
                  <i className="mdi mdi-loading mdi-spin" style={{ fontSize: 14 }} /> Generating…
                </>
              ) : (
                <>
                  <i className="mdi mdi-auto-fix" style={{ fontSize: 14 }} />{" "}
                  {generated ? "Regenerate" : "Create Payment Link"}
                </>
              )}
            </button>

            {/* Copy + Go to Checkout — only shown after generation */}
            {generated && (
              <>
                <button
                  className="cp-bar-btn"
                  onClick={() => copyText(getLink())}
                  title="Copy Link"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 9,
                    border: "none",
                    background: "var(--btn-color)",
                    color: "#fff",
                    fontSize: 14,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all .2s",
                    flexShrink: 0,
                  }}
                >
                  <i className={`mdi ${copied ? "mdi-check" : "mdi-content-copy"}`} />
                </button>
                <button
                  className="cp-bar-btn"
                  onClick={() => window.open(getLink(), "_blank")}
                  title="Go to Checkout"
                  style={{
                    padding: "9px 14px",
                    borderRadius: 9,
                    border: "none",
                    background: "var(--btn-color)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    transition: "all .2s",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Go to Checkout <i className="mdi mdi-arrow-right" style={{ fontSize: 14 }} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
}
